'use client'

import React, { Suspense, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { TimeSeriesPoint } from '@/types/usaspending'

import { SpendingTrendChart } from '../../charts/SpendingTrendChart'
import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { ChartUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * Get the fiscal year for a given calendar year and month.
 * Federal fiscal years run Oct-Sep (e.g., FY2024 = Oct 2023 - Sep 2024).
 */
function getFiscalYear(year: number, month: number): number {
  return month >= 10 ? year + 1 : year
}

/**
 * RecipientDetail.SpendingTrend
 *
 * Area chart showing monthly spending (FY2023+).
 * Full-width section with gradient fill and hover tooltips.
 */
export function SpendingTrendSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="spending-trend"
      title="Spending Trend"
      subtitle="Monthly, FY2023 onwards"
      fullWidth
      animationDelay={0}
    >
      <QueryErrorBoundary
        title="Failed to load spending trend"
        serviceUnavailableFallback={<ChartUnavailable height={200} />}
      >
        <Suspense fallback={<SectionSkeleton height={200} bars={0} />}>
          <SpendingTrendContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function SpendingTrendContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const { data: dashboard } = useSuspenseRecipientDashboard(id)

  if (dashboard.timeSeries === null) {
    return <DeferredSpendingTrend id={id} />
  }

  const data = dashboard.timeSeries
  return <SpendingTrendInner data={data} fiscalYear={state.fiscalYear} />
}

function DeferredSpendingTrend({ id }: { id: string }) {
  const queries = useRecipientAnalyticsQueries()
  const { data, isLoading } = useQuery(queries.timeSeries(id))

  if (isLoading) return <SectionSkeleton height={200} bars={0} />
  if (!data) return null

  return <SpendingTrendInner data={data} fiscalYear="all" />
}

function SpendingTrendInner({
  data,
  fiscalYear,
}: {
  data: TimeSeriesPoint[]
  fiscalYear: number | 'all'
}) {
  const chartRef = useRef<HTMLDivElement>(null)
  const { registerChartRef } = useRecipientDetail()

  // Register chart ref for PDF export capture
  useEffect(() => {
    const element = chartRef.current
    if (element) {
      registerChartRef('spendingTrend', element)
    }
    return () => {
      registerChartRef('spendingTrend', null)
    }
  }, [registerChartRef])

  // Filter by fiscal year if selected (FY runs Oct-Sep)
  const filteredData =
    fiscalYear === 'all'
      ? data
      : data.filter((d) => getFiscalYear(d.year, d.month) === fiscalYear)

  return (
    <div ref={chartRef}>
      <SpendingTrendChart data={filteredData} height={200} accentColor="blue" />
    </div>
  )
}
